import Wishlist from '@theme/wishlist';

/**
 * `<wishlist-icon>` — the header heart with a live saved-count bubble.
 *
 * The count lives in localStorage, so it can't be rendered server-side. On
 * connect (and on every change) we read the shared `Wishlist` store and update
 * the bubble, hiding it when the wishlist is empty.
 */
class WishlistIcon extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** Whether the saved state has been applied once and painted. @type {boolean} */
  #ready = false;

  connectedCallback() {
    this.bubble = this.querySelector('.wishlist-bubble');
    this.count = this.querySelector('.wishlist-bubble__count');

    this.#unsubscribe = Wishlist.subscribe(() => this.#render());
    this.#render();
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
  }

  #render() {
    const count = Wishlist.getCount();
    if (this.count) this.count.textContent = count < 100 ? String(count) : '99+';
    if (this.bubble) this.bubble.classList.toggle('visually-hidden', count === 0);
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);

    // The saved state is only known once this element connects, so on every page load the
    // heart would fade from outline to the accent colour as if the shopper had just saved
    // something. The fill transition is gated on this class in the stylesheet: enabling it
    // only after the initial state has painted keeps the first render instant and reserves
    // the fade for changes made while the page is open. Two frames because the class must
    // land after the browser has painted, not merely after the current task.
    if (!this.#ready) {
      this.#ready = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('header-actions__wishlist-icon--ready'));
      });
    }
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

export default WishlistIcon;
